import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Send, Loader2, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const ChatModal = ({
  receiverId,
  receiverName = "Artiste",
  contextType = "general",
  contextId = "",
  contextTitle = "",
  onClose,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => {
    api
      .get(`/messages/thread/${receiverId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverId, user]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post("/messages", {
        receiver_id: receiverId,
        content: text.trim(),
        context_type: contextType,
        context_id: contextId,
        context_title: contextTitle,
      });
      setText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="chat-modal"
    >
      <div className="bg-[#0d0d0d] border border-white/15 w-full max-w-lg flex flex-col h-[70vh] max-h-[640px]">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-5 w-5 text-[#60a5fa]" />
            <div>
              <p className="text-white font-display text-lg leading-none">{receiverName}</p>
              {contextTitle && <p className="text-zinc-500 text-xs mt-1">À propos de « {contextTitle} »</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="close-chat-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!user ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-5">
            <p className="text-zinc-300">Connectez-vous pour envoyer un message à {receiverName}.</p>
            <button
              data-testid="chat-login-cta"
              onClick={() => navigate("/connexion")}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 py-3 text-sm transition-colors"
            >
              Se connecter
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="chat-thread">
              {loading ? (
                <p className="text-zinc-500 text-sm text-center mt-6">Chargement…</p>
              ) : messages.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center mt-6">
                  Aucun message. Écrivez le premier message à {receiverName}.
                </p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed ${
                        m.mine ? "bg-[#2563EB] text-white" : "bg-white/5 text-zinc-200 border border-white/10"
                      }`}
                    >
                      {m.content}
                      <span className="block text-[10px] opacity-60 mt-1">
                        {new Date(m.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={endRef} />
            </div>

            <div className="p-4 border-t border-white/10 flex items-center gap-3">
              <input
                data-testid="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Votre message…"
                className="flex-1 bg-black border border-white/15 px-4 py-3 text-white text-sm focus:border-[#2563EB] outline-none"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={sending || !text.trim()}
                className="inline-flex items-center justify-center bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white h-11 w-11 transition-colors"
                aria-label="Envoyer"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
